import React from 'react'
import i18n from 'i18next'

interface Props {
  children: React.ReactNode
}

interface State {
  failed: boolean
}

/**
 * Keeps one unreadable correction from taking the page down with it.
 *
 * React unmounts the whole tree on an uncaught render error, and this app has
 * no boundary above the router, so a single malformed row rendered as a blank
 * white page — no list, no header, no way back. That is exactly how a teacher
 * closing a correction without writing anything reached support as "I click
 * CORRECTIONS and the screen goes blank".
 *
 * The row that failed says so and the rest of the list still renders. The error
 * is re-thrown to `console.error` on the way past so it still reaches
 * PostHog's exception capture rather than being silently swallowed.
 */
class CorrectionCardBoundary extends React.Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Correction card failed to render', error, info)
  }

  render() {
    if (this.state.failed) {
      return (
        <div className='correction-item'>
          <div className='w-row w-d-flex section-story-content mb-20'>
            <div className='w-col-12 content-left box-padding border-full border-radius-10'>
              <p className='cx-quiet'>{i18n.t('corrections:boundary.failed')}</p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default CorrectionCardBoundary
